import { z } from "zod";
import { confirmableConfig, confirmableDefaults } from "./confirmable.js";
import { optionalUnlessRequired, requiredText, type FieldDescriptor } from "./types.js";

// Pattern + custom error message — for matric numbers, staff IDs, exam numbers
// (docs/01-PRODUCT-SPEC.md §3.2).
const configSchema = z.object({
  ...confirmableConfig,
  pattern: z.string().min(1).default(".*"),
  errorMessage: z.string().max(300).default("That value is not in the expected format"),
  placeholderExample: z.string().max(100).optional(),
  /**
   * Key of another field this one must equal — the "Confirm account number" / "Confirm NIN"
   * pattern. Checked in `buildFormValidator`, so the browser and the server enforce it from the
   * same place. A mistyped account number is not a cosmetic error: it sends a stipend to a
   * stranger.
   */
  mustMatchKey: z.string().max(100).optional(),
});

export const textRegexField: FieldDescriptor<z.infer<typeof configSchema>> = {
  type: "text_regex",
  label: "Pattern-matched text",
  category: "text",
  icon: "Regex",
  configSchema,
  valueSchema: (config, isRequired) => {
    let compiled: RegExp;
    try {
      compiled = new RegExp(config.pattern);
    } catch {
      // A pattern that does not compile is a form-configuration error, not an applicant error.
      // Fail closed rather than silently accepting anything: draft validation
      // (POST /forms/:id/draft/validate) is where the author gets told about it.
      compiled = /(?!)/;
    }
    const base = (isRequired ? requiredText() : z.string().trim()).regex(
      compiled,
      config.errorMessage,
    );
    return optionalUnlessRequired(base, isRequired);
  },
  defaultConfig: {
    ...confirmableDefaults,
    pattern: ".*",
    errorMessage: "That value is not in the expected format",
  },
  supportsProfileBinding: true,
  defaultSearchable: true,
  renderHints: {},
};
